import { motion } from 'framer-motion';
import { CheckCircle2, ChevronDown, ChevronRight, ClipboardList, Loader2, PlusCircle, Sparkles } from 'lucide-react';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FieldArrayWithId, UseFieldArrayRemove } from 'react-hook-form';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useDeleteMedicalProviderMutation } from '@/services/api/treatmentService';
import type { ReviewFoundProviderEntry } from '@/services/types/treatment-types';
import {
  CheckInObjectiveResponse,
  CheckInObjectiveType,
  ProviderTreatmentStatus,
} from '@/services/types/treatment-types';

import { treatmentStatusInfo } from '../constants';
import { ObjectiveCard } from './ObjectiveCard';
import {
  shouldShowAppointmentObjective,
  shouldShowProviderAdditionalNotes,
  shouldShowSecondaryProviderObjectives,
} from './objectiveUi';
import { ReviewProviderCard } from './ReviewProviderCard';
import type { EditCheckInFormValues } from './types';

interface ProviderGroup {
  providerId: string;
  providerName: string;
  /** Indices into the objectives field array */
  objectiveIndices: number[];
}

interface ObjectivesSectionProps {
  disabled?: boolean;
  isLoading?: boolean;
  /** Fields from useFieldArray for objectives, managed by the parent */
  objectiveFields: FieldArrayWithId<EditCheckInFormValues, 'objectives', 'id'>[];
  /** Map from providerId to provider_updates array index */
  providerIndexMap: Map<string, number>;
  /** Review providers needing approval */
  reviewFoundProviders: ReviewFoundProviderEntry[];
  onApproveProvider: (providerId: string) => void;
  onRejectProvider: (providerId: string) => void;
  isApprovingProvider: boolean;
  isRejectingProvider: boolean;
  onAddProvider: () => void;
  /** Set of error keys (e.g. 'provider:{id}') for validation shake */
  validationErrors?: Set<string>;
  /** IDs of previously completed providers to exclude from active groups */
  previouslyCompletedProviderIds?: Set<string>;
  /** Remove objectives by indices (from useFieldArray) */
  removeObjective: UseFieldArrayRemove;
  /** Remove a provider update by index (from useFieldArray) */
  removeProviderUpdate: UseFieldArrayRemove;
  /** Matter ID for delete provider mutation */
  matterId: string;
}

export const ObjectivesSection: React.FC<ObjectivesSectionProps> = ({
  disabled = false,
  isLoading = false,
  objectiveFields,
  providerIndexMap,
  reviewFoundProviders,
  onApproveProvider,
  onRejectProvider,
  isApprovingProvider,
  isRejectingProvider,
  onAddProvider,
  validationErrors,
  previouslyCompletedProviderIds = new Set(),
  removeObjective,
  removeProviderUpdate,
  matterId,
}) => {
  const { control, setValue } = useFormContext<EditCheckInFormValues>();
  const [deleteProvider] = useDeleteMedicalProviderMutation();
  const providerUpdates = useWatch({ control, name: 'provider_updates' });
  const objectives = useWatch({ control, name: 'objectives' });

  // Track which completed providers are expanded
  const [expandedCompleted, setExpandedCompleted] = useState<Set<string>>(
    new Set(),
  );

  // Track whether the review found providers list is expanded (collapsed by default when > 3)
  const [reviewProvidersExpanded, setReviewProvidersExpanded] = useState(false);

  // Track providers transitioning to completed.
  const [transitioning, setTransitioning] = useState<Set<string>>(new Set());
  // Store a map of providerId → status STRING (primitive values, not object refs)
  // to avoid issues with react-hook-form potentially reusing the same array reference.
  const prevStatusMapRef = useRef<Map<string, string | null | undefined>>(
    new Map(),
  );
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Track previous still-treating responses so the sync effect only fires
  // for providers whose response actually changed (not ALL providers on every
  // objectives change). This prevents manual dropdown changes from being
  // overridden when an unrelated provider's objective changes.
  const prevStillTreatingRespRef = useRef<Map<string, string | undefined>>(
    new Map(),
  );

  // Track each provider's initial treatment status (captured once on first render).
  // Used by DNA to restore the provider to its original status.
  const initialStatusMapRef = useRef<Map<string, string | null>>(new Map());
  const initialOccurredAtMapRef = useRef<Map<string, string | null>>(new Map());

  // Never Treated confirmation dialog state
  const [neverTreatedDialog, setNeverTreatedDialog] = useState<{
    providerId: string;
    providerName: string;
  } | null>(null);

  const handleConfirmNeverTreated = useCallback(async () => {
    if (!neverTreatedDialog) return;
    const { providerId } = neverTreatedDialog;
    try {
      await deleteProvider({ matterId, providerId }).unwrap();
      // Remove objectives for this provider (remove from highest index first to avoid index shifts)
      const objIndicesToRemove = objectiveFields
        .map((f, i) => (f.providerId === providerId ? i : -1))
        .filter((i) => i >= 0)
        .reverse();
      objIndicesToRemove.forEach((i) => removeObjective(i));
      // Remove provider update
      const providerIdx = providerIndexMap.get(providerId);
      if (providerIdx !== undefined) {
        removeProviderUpdate(providerIdx);
      }
    } catch {
      // Deletion failed — the status dropdown was not changed (NEVER_TREATED is intercepted)
    }
    setNeverTreatedDialog(null);
  }, [
    neverTreatedDialog,
    deleteProvider,
    matterId,
    objectiveFields,
    removeObjective,
    providerIndexMap,
    removeProviderUpdate,
  ]);

  const toggleCompleted = (providerId: string) => {
    setExpandedCompleted((prev) => {
      const next = new Set(prev);
      if (next.has(providerId)) {
        next.delete(providerId);
      } else {
        next.add(providerId);
      }
      return next;
    });
  };

  // Collect matter-level (non-provider) objective indices
  const matterObjectiveIndices = useMemo(
    () =>
      objectiveFields
        .map((field, index) => (!field.providerId ? index : -1))
        .filter((i) => i >= 0),
    [objectiveFields],
  );

  // Group objectives by provider
  const providerGroups = useMemo(() => {
    const groupMap = new Map<string, ProviderGroup>();

    objectiveFields.forEach((field, index) => {
      const pid = field.providerId;
      if (!pid) return;

      if (!groupMap.has(pid)) {
        groupMap.set(pid, {
          providerId: pid,
          providerName: field.providerName ?? 'Unknown Provider',
          objectiveIndices: [],
        });
      }
      const group = groupMap.get(pid);
      if (group) {
        group.objectiveIndices.push(index);
      }
    });

    return Array.from(groupMap.values());
  }, [objectiveFields]);

  // Capture each provider's initial treatment status on first render.
  // This runs once (when providerUpdates first populates) so DNA can
  // restore a provider to its original status if the user toggles away.
  useLayoutEffect(() => {
    if (!providerUpdates || initialStatusMapRef.current.size > 0) return;
    providerGroups.forEach((group) => {
      const idx = providerIndexMap.get(group.providerId);
      if (idx !== undefined) {
        initialStatusMapRef.current.set(
          group.providerId,
          providerUpdates[idx]?.newTreatmentStatus ?? null,
        );
        initialOccurredAtMapRef.current.set(
          group.providerId,
          providerUpdates[idx]?.newTreatmentStatusOccurredAt ?? null,
        );
      }
    });
  }, [providerUpdates, providerGroups, providerIndexMap]);

  // Detect transitions using useLayoutEffect — runs synchronously after DOM
  // mutation but BEFORE the browser paints. This means even though the first
  // render might sort the provider into the completed section, the
  // setTransitioning call triggers an immediate synchronous re-render that
  // corrects the sort order before anything is painted to screen.
  //
  // We compare primitive string values (not object references) because
  // react-hook-form's useWatch may return the same array reference with
  // mutated contents, which would make ref-based comparisons always equal.
  useLayoutEffect(() => {
    if (!providerUpdates) return;

    const newStatusMap = new Map<string, string | null | undefined>();
    const newlyTransitioning = new Set<string>();

    providerGroups.forEach((group) => {
      const idx = providerIndexMap.get(group.providerId);
      if (idx === undefined) return;

      const currStatus = providerUpdates[idx]?.newTreatmentStatus ?? null;
      newStatusMap.set(group.providerId, currStatus);

      const prevStatus = prevStatusMapRef.current.get(group.providerId);

      // Only detect transitions after initial population (prevStatus !== undefined).
      // Skip the transition animation when still-treating was answered NO —
      // we keep the card open so the user can enter an end date + notes.
      if (
        prevStatus !== undefined &&
        currStatus === ProviderTreatmentStatus.COMPLETED &&
        prevStatus !== ProviderTreatmentStatus.COMPLETED &&
        !isStillTreatingNo(group)
      ) {
        newlyTransitioning.add(group.providerId);
      }
    });

    prevStatusMapRef.current = newStatusMap;

    if (newlyTransitioning.size === 0) return;

    setTransitioning((p) => new Set([...p, ...newlyTransitioning]));

    // Clear the transitioning state after animation completes
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setTransitioning((p) => {
        const next = new Set(p);
        newlyTransitioning.forEach((id) => next.delete(id));
        return next;
      });
    }, 1200);

    // eslint-disable-next-line consistent-return
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerUpdates, providerGroups, providerIndexMap]);

  // Sync provider treatment status based on still-treating response.
  // This runs at the ObjectivesSection level (always mounted) rather than
  // inside ObjectiveCard, because switching from No→Yes would collapse the
  // card before the ObjectiveCard effect could fire.
  //
  // YES → TREATING, NO → COMPLETED, DNA → restore to original status.
  //
  // We track previous responses so we only fire setValue for providers whose
  // still-treating response actually changed. This prevents manual dropdown
  // changes from being overridden when an unrelated provider's objective
  // changes and the effect re-runs.
  useLayoutEffect(() => {
    if (!objectives) return;

    const newRespMap = new Map<string, string | undefined>();

    providerGroups.forEach((group) => {
      const stillTreatingIdx = group.objectiveIndices.find(
        (idx) =>
          objectives[idx]?.objectiveType ===
          CheckInObjectiveType.CONFIRM_STILL_TREATING,
      );
      if (stillTreatingIdx === undefined) return;

      const resp = objectives[stillTreatingIdx]?.response;
      newRespMap.set(group.providerId, resp);

      const prevResp = prevStillTreatingRespRef.current.get(group.providerId);

      // Skip if the response hasn't changed (prevents overriding manual dropdown)
      if (prevResp !== undefined && prevResp === resp) return;

      const providerIdx = providerIndexMap.get(group.providerId);
      if (providerIdx === undefined) return;

      if (resp === CheckInObjectiveResponse.YES) {
        setValue(
          `provider_updates.${providerIdx}.newTreatmentStatus`,
          ProviderTreatmentStatus.TREATING,
          { shouldDirty: true },
        );
        const originalOccurredAt = initialOccurredAtMapRef.current.get(
          group.providerId,
        );
        if (
          !providerUpdates?.[providerIdx]?.newTreatmentStatusOccurredAt &&
          originalOccurredAt
        ) {
          setValue(
            `provider_updates.${providerIdx}.newTreatmentStatusOccurredAt`,
            originalOccurredAt,
            { shouldDirty: true },
          );
        }
      } else if (resp === CheckInObjectiveResponse.NO) {
        setValue(
          `provider_updates.${providerIdx}.newTreatmentStatus`,
          ProviderTreatmentStatus.COMPLETED,
          { shouldDirty: true },
        );
        setValue(
          `provider_updates.${providerIdx}.newTreatmentStatusOccurredAt`,
          null,
          { shouldDirty: true },
        );
      } else if (resp === CheckInObjectiveResponse.COULD_NOT_COLLECT) {
        // DNA: restore to original status from when the form loaded
        const original = initialStatusMapRef.current.get(group.providerId);
        setValue(
          `provider_updates.${providerIdx}.newTreatmentStatus`,
          (original as ProviderTreatmentStatus) ?? ProviderTreatmentStatus.TREATING,
          { shouldDirty: true },
        );
        const originalOccurredAt = initialOccurredAtMapRef.current.get(
          group.providerId,
        );
        setValue(
          `provider_updates.${providerIdx}.newTreatmentStatusOccurredAt`,
          originalOccurredAt ?? null,
          { shouldDirty: true },
        );
      }
    });

    prevStillTreatingRespRef.current = newRespMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectives, providerGroups, providerIndexMap, providerUpdates, setValue]);

  // Check if a group's still-treating objective was answered NO.
  // When it is, we keep the card open (no transition, no collapse)
  // so the user can enter an end date and notes.
  const getStillTreatingResponse = (
    group: ProviderGroup,
  ): CheckInObjectiveResponse | undefined => {
    const stillTreatingIdx = group.objectiveIndices.find(
      (idx) =>
        objectives?.[idx]?.objectiveType ===
        CheckInObjectiveType.CONFIRM_STILL_TREATING,
    );
    return stillTreatingIdx !== undefined
      ? objectives?.[stillTreatingIdx]?.response
      : undefined;
  };

  const isStillTreatingNo = (group: ProviderGroup): boolean =>
    getStillTreatingResponse(group) === CheckInObjectiveResponse.NO;

  const showProviderNotes = (group: ProviderGroup): boolean =>
    shouldShowProviderAdditionalNotes(getStillTreatingResponse(group));

  const getObjectiveResponse = (
    group: ProviderGroup,
    type: CheckInObjectiveType,
  ): CheckInObjectiveResponse | undefined => {
    const objectiveIdx = group.objectiveIndices.find(
      (idx) => objectives?.[idx]?.objectiveType === type,
    );
    return objectiveIdx !== undefined
      ? objectives?.[objectiveIdx]?.response
      : undefined;
  };

  // Helper to check if a group's provider is completed
  const isGroupCompleted = (group: ProviderGroup): boolean => {
    const providerIdx = providerIndexMap.get(group.providerId);
    const status =
      providerIdx !== undefined
        ? providerUpdates?.[providerIdx]?.newTreatmentStatus
        : undefined;
    return status === ProviderTreatmentStatus.COMPLETED;
  };

  // Compute visibility for objectives within a provider group.
  // Objectives that don't match the provider's current status are hidden
  // (e.g. completion-date when status changed to TREATING, or treating
  // objectives when status changed to COMPLETED).
  const isObjectiveVisible = (group: ProviderGroup, objIndex: number): boolean => {
    const type = objectives?.[objIndex]?.objectiveType;
    const providerIdx = providerIndexMap.get(group.providerId);
    const currentStatus =
      providerIdx !== undefined
        ? providerUpdates?.[providerIdx]?.newTreatmentStatus
        : undefined;
    const stillTreatingResponse = getObjectiveResponse(
      group,
      CheckInObjectiveType.CONFIRM_STILL_TREATING,
    );
    const firstVisitResponse = getObjectiveResponse(
      group,
      CheckInObjectiveType.CONFIRM_FIRST_VISIT,
    );

    // Hide completion-date when provider is no longer COMPLETED
    if (
      type === CheckInObjectiveType.CONFIRM_COMPLETION_DATE &&
      currentStatus &&
      currentStatus !== ProviderTreatmentStatus.COMPLETED
    ) {
      return false;
    }

    // Hide treating-detail objectives when provider is COMPLETED, DEVIATING, or REFERRED_TO.
    // CONFIRM_STILL_TREATING is intentionally excluded — it serves as the primary
    // status toggle and must remain visible so users can change their answer.
    if (
      currentStatus &&
      currentStatus !== ProviderTreatmentStatus.TREATING &&
      currentStatus !== ProviderTreatmentStatus.UNKNOWN &&
      (type === CheckInObjectiveType.CONFIRM_TREATMENT_FREQUENCY ||
        type === CheckInObjectiveType.CONFIRM_FIRST_VISIT ||
        type === CheckInObjectiveType.CONFIRM_APPOINTMENT_DATE)
    ) {
      return false;
    }

    // Always show still-treating, completion-date, and other objectives
    if (
      type === CheckInObjectiveType.CONFIRM_STILL_TREATING ||
      type === CheckInObjectiveType.CONFIRM_COMPLETION_DATE ||
      type === CheckInObjectiveType.OTHER
    ) {
      return true;
    }

    if (type === CheckInObjectiveType.CONFIRM_APPOINTMENT_DATE) {
      return shouldShowAppointmentObjective(
        stillTreatingResponse,
        firstVisitResponse,
      );
    }

    return shouldShowSecondaryProviderObjectives(stillTreatingResponse);
  };

  // Sort: active + transitioning providers first, completed at bottom.
  // Transitioning providers stay in-place until animation finishes.
  // Still-treating "No" providers stay in the active section so
  // the user can enter end date + notes before the card collapses.
  const sortedGroups = useMemo(() => {
    const active = providerGroups.filter(
      (g) =>
        !isGroupCompleted(g) ||
        transitioning.has(g.providerId) ||
        isStillTreatingNo(g),
    );
    const completed = providerGroups.filter(
      (g) =>
        isGroupCompleted(g) &&
        !transitioning.has(g.providerId) &&
        !isStillTreatingNo(g),
    );
    return [...active, ...completed];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerGroups, providerUpdates, providerIndexMap, transitioning, objectives]);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-3">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Generating objectives...</span>
        </div>
      </div>
    );
  }

  const hasContent = objectiveFields.length > 0 || reviewFoundProviders.length > 0;

  if (!hasContent) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-3">
        <div className="flex items-center justify-between">
          <p className="text-xs text-zinc-500">
            No providers in this check-in yet.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={onAddProvider}
            size="sm"
          >
            <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
            Add Provider
          </Button>
        </div>
      </div>
    );
  }

  // Split groups into: active, completed-during-checkin, previously-completed
  const activeGroups = sortedGroups.filter(
    (g) =>
      !previouslyCompletedProviderIds.has(g.providerId) &&
      (!isGroupCompleted(g) ||
        transitioning.has(g.providerId) ||
        isStillTreatingNo(g)),
  );
  const completedDuringCheckinGroups = sortedGroups.filter(
    (g) =>
      !previouslyCompletedProviderIds.has(g.providerId) &&
      isGroupCompleted(g) &&
      !transitioning.has(g.providerId) &&
      !isStillTreatingNo(g),
  );
  const previouslyCompletedGroups = sortedGroups.filter((g) =>
    previouslyCompletedProviderIds.has(g.providerId),
  );

  const renderProviderGroup = (group: ProviderGroup) => {
    const providerIdx = providerIndexMap.get(group.providerId);
    const providerUpdate =
      providerIdx !== undefined
        ? providerUpdates?.[providerIdx]
        : undefined;
    const statusKey = providerUpdate?.newTreatmentStatus;
    const isCompleted =
      statusKey === ProviderTreatmentStatus.COMPLETED;
    const isExpanded = expandedCompleted.has(group.providerId);
    const isTransitioning = transitioning.has(group.providerId);

    // Transitioning to completed — show a "Marked as Completed" card
    // that fades out before the collapsed row appears.
    if (isTransitioning) {
      const statusInfo =
        treatmentStatusInfo[ProviderTreatmentStatus.COMPLETED];
      return (
        <motion.div
          key={`${group.providerId}-transitioning`}
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-3"
        >
          <div className="flex items-center gap-2">
            <statusInfo.icon
              className={`h-4 w-4 ${statusInfo.color}`}
            />
            <span className="text-xs font-semibold text-zinc-900">
              {group.providerName}
            </span>
            <Badge
              variant="secondary"
              className="text-[10px] px-1.5 py-0 gap-1 bg-emerald-100 text-emerald-700"
            >
              Marked as Completed
            </Badge>
          </div>
        </motion.div>
      );
    }

    // Collapsed completed provider row (skip if still-treating No — card stays open)
    if (isCompleted && !isExpanded && !isStillTreatingNo(group)) {
      const statusInfo =
        treatmentStatusInfo[ProviderTreatmentStatus.COMPLETED];
      return (
        <motion.button
          key={group.providerId}
          type="button"
          onClick={() => toggleCompleted(group.providerId)}
          className="flex w-full items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50/50 px-3 py-2 text-left hover:bg-zinc-100/50 transition-colors"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <ChevronRight className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
          <span className="text-xs font-semibold text-zinc-900">
            {group.providerName}
          </span>
          <Badge
            variant="secondary"
            className="text-[10px] px-1.5 py-0 gap-1"
          >
            <statusInfo.icon
              className={`h-3 w-3 ${statusInfo.color}`}
            />
            {statusInfo.label}
          </Badge>
          <span className="ml-auto text-[10px] text-zinc-400">
            {group.objectiveIndices.length} objective
            {group.objectiveIndices.length !== 1 ? 's' : ''}
          </span>
        </motion.button>
      );
    }

    const hasProviderError = validationErrors?.has(
      `provider:${group.providerId}`,
    );

    return (
      <motion.div
        key={group.providerId}
        className={cn(
          'rounded-lg border bg-white',
          hasProviderError ? 'border-red-500' : 'border-zinc-200',
        )}
        animate={
          hasProviderError ? { x: [0, -6, 6, -6, 6, 0] } : { x: 0 }
        }
        transition={hasProviderError ? { duration: 0.4 } : undefined}
      >
        {/* Provider Header — only show collapse toggle when expanded AND completed
            (not when still-treating = No, which keeps the card open for data entry) */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100">
          {isCompleted && isExpanded && !isStillTreatingNo(group) && (
            <button
              type="button"
              onClick={() => toggleCompleted(group.providerId)}
              className="shrink-0"
            >
              <ChevronRight className="h-3.5 w-3.5 text-zinc-400 rotate-90" />
            </button>
          )}
          <span className="text-xs font-semibold text-zinc-900">
            {group.providerName}
          </span>
          {/* Treatment Status Dropdown */}
          {providerIdx !== undefined && (
            <Controller
              name={`provider_updates.${providerIdx}.newTreatmentStatus`}
              control={control}
              render={({ field }) => {
                const currentInfo = field.value
                  ? treatmentStatusInfo[field.value]
                  : undefined;
                return (
                  <Select
                    value={field.value ?? undefined}
                    onValueChange={(val) => {
                      if (val === ProviderTreatmentStatus.NEVER_TREATED) {
                        setNeverTreatedDialog({
                          providerId: group.providerId,
                          providerName: group.providerName,
                        });
                        return;
                      }
                      field.onChange(val as ProviderTreatmentStatus);
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger className="ml-auto h-7 w-[150px] gap-1.5 text-xs px-2 border-zinc-200">
                      {currentInfo ? (
                        <div className="flex items-center gap-1.5 whitespace-nowrap min-w-0">
                          <currentInfo.icon
                            className={`h-3 w-3 shrink-0 ${currentInfo.color}`}
                          />
                          <span>{currentInfo.label}</span>
                        </div>
                      ) : (
                        <SelectValue placeholder="Set status" />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(treatmentStatusInfo).map(
                        ([key, info]) => (
                          <SelectItem key={key} value={key}>
                            <span className="flex items-center gap-1.5 text-xs">
                              <info.icon
                                className={`h-3 w-3 shrink-0 ${info.color}`}
                              />
                              <span>{info.label}</span>
                            </span>
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                );
              }}
            />
          )}
        </div>

        {/* Objective Cards — sequential reveal */}
        <div className="px-3 divide-y divide-zinc-100">
          {group.objectiveIndices.map((objIndex) => {
            const visible = isObjectiveVisible(group, objIndex);
            if (!visible) return null;
            return (
              <div key={objectiveFields[objIndex]?.id}>
                <ObjectiveCard
                  index={objIndex}
                  disabled={disabled}
                  providerIndexMap={providerIndexMap}
                />
              </div>
            );
          })}
        </div>

        {/* Treatment Notes — only after a definitive still-treating answer */}
        {providerIdx !== undefined && showProviderNotes(group) && (
          <div className="px-3 pb-2">
            <Controller
              name={`provider_updates.${providerIdx}.newTreatmentNotes`}
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  value={field.value ?? ''}
                  rows={1}
                  maxLength={2000}
                  placeholder="Additional notes..."
                  className="resize-none text-xs"
                  disabled={disabled}
                />
              )}
            />
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Review Found Providers Section */}
      {reviewFoundProviders.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-xs font-semibold text-zinc-700">
              Review Found Providers
            </span>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700">
              {reviewFoundProviders.length}
            </Badge>
          </div>
          {(reviewFoundProviders.length <= 3 || reviewProvidersExpanded
            ? reviewFoundProviders
            : reviewFoundProviders.slice(0, 3)
          ).map((rfp) => (
            <ReviewProviderCard
              key={rfp.medical_provider_id}
              provider={rfp}
              onApprove={onApproveProvider}
              onReject={onRejectProvider}
              isApproving={isApprovingProvider}
              isRejecting={isRejectingProvider}
            />
          ))}
          {reviewFoundProviders.length > 3 && (
            <button
              type="button"
              onClick={() => setReviewProvidersExpanded((prev) => !prev)}
              className="flex w-full items-center justify-center gap-1 rounded-md border border-amber-200 bg-amber-50/50 px-2 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100/50 transition-colors"
            >
              {reviewProvidersExpanded ? (
                <>
                  <ChevronDown className="h-3 w-3" />
                  Show fewer
                </>
              ) : (
                <>
                  <ChevronRight className="h-3 w-3" />
                  Show {reviewFoundProviders.length - 3} more
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Call Objectives — matter-level (task-based + AI) */}
      {matterObjectiveIndices.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-3.5 w-3.5 text-indigo-500" />
            <span className="text-xs font-semibold text-zinc-700">
              Call Objectives
            </span>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-indigo-100 text-indigo-700">
              {matterObjectiveIndices.length}
            </Badge>
          </div>
          <motion.div
            className={cn(
              'rounded-lg border bg-white px-3 divide-y divide-zinc-100',
              validationErrors?.has('call-objectives') ? 'border-red-500' : 'border-zinc-200',
            )}
            animate={
              validationErrors?.has('call-objectives') ? { x: [0, -6, 6, -6, 6, 0] } : { x: 0 }
            }
            transition={validationErrors?.has('call-objectives') ? { duration: 0.4 } : undefined}
          >
            {matterObjectiveIndices.map((objIndex) => (
              <ObjectiveCard
                key={objectiveFields[objIndex]?.id}
                index={objIndex}
                disabled={disabled}
                providerIndexMap={providerIndexMap}
              />
            ))}
          </motion.div>
        </div>
      )}

      {/* Current Providers Section */}
      {(activeGroups.length > 0 || completedDuringCheckinGroups.length > 0) && (
        <div className="space-y-2">
          <span className="text-xs font-semibold text-zinc-700">
            Current Providers
          </span>
          {activeGroups.map(renderProviderGroup)}
          {completedDuringCheckinGroups.map(renderProviderGroup)}
        </div>
      )}

      {/* Add Provider Button */}
      <Button
        type="button"
        variant="outline"
        onClick={onAddProvider}
        size="sm"
        className="w-full"
      >
        <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
        Add Provider
      </Button>

      {/* Previously Completed Providers Section */}
      {previouslyCompletedGroups.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
            <span className="text-xs font-semibold text-zinc-500">
              Previously Completed
            </span>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              {previouslyCompletedGroups.length}
            </Badge>
          </div>
          {previouslyCompletedGroups.map(renderProviderGroup)}
        </div>
      )}

      {/* Never Treated confirmation dialog */}
      <AlertDialog
        open={!!neverTreatedDialog}
        onOpenChange={(open) => {
          if (!open) setNeverTreatedDialog(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {neverTreatedDialog?.providerName}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this provider from the case.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmNeverTreated}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove Provider
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
